import React, { useState } from 'react'
import { X, Upload, AlertCircle, CheckCircle, Send, Camera } from 'lucide-react'

const SupportForm = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    priority: 'medium',
    screenshot: null
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const priorityOptions = [
    { value: 'low',    label: 'Low'    },
    { value: 'medium', label: 'Medium' },
    { value: 'high',   label: 'High'   },
    { value: 'urgent', label: 'Urgent' }
  ]

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setError('')
  }

  const handleFileSelect = (file) => {
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file (PNG, JPG, GIF, WEBP)')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB')
        return
      }
      setFormData(prev => ({ ...prev, screenshot: file }))
      setError('')
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    handleFileSelect(file)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    handleFileSelect(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setDragOver(false)
  }

  const removeScreenshot = () => {
    setFormData(prev => ({ ...prev, screenshot: null }))
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.subject.trim()) {
      setError('Subject is required')
      return
    }
    if (!formData.message.trim()) {
      setError('Message is required')
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      const submitData = new FormData()
      submitData.append('subject', formData.subject.trim())
      submitData.append('message', formData.message.trim())
      submitData.append('priority', formData.priority)
      if (formData.screenshot) {
        submitData.append('screenshot', formData.screenshot)
      }

      await onSubmit(submitData)

      setSuccess(true)

      setTimeout(() => {
        setFormData({ subject: '', message: '', priority: 'medium', screenshot: null })
        setSuccess(false)
        onClose()
      }, 2000)

    } catch (err) {
      setError(err.message || 'Failed to submit support request. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const takeScreenshot = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: { mediaSource: 'screen' }
        })

        const video = document.createElement('video')
        video.srcObject = stream
        video.play()

        video.addEventListener('loadedmetadata', () => {
          const canvas = document.createElement('canvas')
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight

          const ctx = canvas.getContext('2d')
          ctx.drawImage(video, 0, 0)

          stream.getTracks().forEach(track => track.stop())

          canvas.toBlob((blob) => {
            const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' })
            handleFileSelect(file)
          }, 'image/png')
        })
      } else {
        setError('Screen capture is not supported in your browser')
      }
    } catch (err) {
      console.error('Screenshot error:', err)
      setError('Failed to capture screenshot. Please try uploading an image instead.')
    }
  }

  if (!isOpen) return null

  const inputStyle = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    background: 'var(--surface)',
    color: 'var(--text)',
    fontSize: '0.875rem',
    outline: 'none',
    boxSizing: 'border-box',
  }

  const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    marginBottom: '0.5rem',
    color: 'var(--text)',
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: '42rem', padding: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem',
          borderBottom: '1px solid var(--border)',
        }}>
          <div className="flex items-center gap-3">
            <div style={{
              width: '2rem',
              height: '2rem',
              background: 'color-mix(in srgb, var(--accent) 15%, transparent)',
              borderRadius: 'var(--radius)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <AlertCircle style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
            </div>
            <h3 className="font-semibold" style={{ fontSize: '1.125rem' }}>
              Need Help?
            </h3>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-icon"
          >
            <X style={{ width: '1.25rem', height: '1.25rem' }} />
          </button>
        </div>

        {/* Success State */}
        {success && (
          <div style={{ padding: '1.5rem', textAlign: 'center' }}>
            <CheckCircle style={{ width: '4rem', height: '4rem', color: 'var(--success)', margin: '0 auto 1rem' }} />
            <h4 className="font-semibold" style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
              Support Request Sent!
            </h4>
            <p className="text-muted">
              We've received your request and will get back to you soon.
            </p>
          </div>
        )}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {/* Error Message */}
            {error && (
              <div style={{
                padding: '1rem',
                background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
                border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
                borderRadius: 'var(--radius)',
              }}>
                <div className="flex items-center gap-2">
                  <AlertCircle style={{ width: '1.25rem', height: '1.25rem', color: 'var(--danger)', flexShrink: 0 }} />
                  <p style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{error}</p>
                </div>
              </div>
            )}

            {/* Subject */}
            <div>
              <label htmlFor="subject" style={labelStyle}>Subject *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="Brief description of your issue"
                maxLength={200}
                style={inputStyle}
                required
              />
              <p className="text-faint" style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}>
                {formData.subject.length}/200 characters
              </p>
            </div>

            {/* Priority */}
            <div>
              <label htmlFor="priority" style={labelStyle}>Priority</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                style={inputStyle}
              >
                {priorityOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" style={labelStyle}>Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Please describe your issue in detail. Include steps to reproduce if applicable."
                rows={6}
                maxLength={5000}
                style={{ ...inputStyle, resize: 'none' }}
                required
              />
              <p className="text-faint" style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}>
                {formData.message.length}/5000 characters
              </p>
            </div>

            {/* Screenshot Upload */}
            <div>
              <label style={labelStyle}>Screenshot (Optional)</label>

              {!formData.screenshot ? (
                <div>
                  {/* Drop Zone */}
                  <div
                    style={{
                      border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border)'}`,
                      borderRadius: 'var(--radius)',
                      padding: '1.5rem',
                      textAlign: 'center',
                      transition: 'border-color 0.2s',
                      background: dragOver ? 'color-mix(in srgb, var(--accent) 5%, transparent)' : 'transparent',
                    }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                  >
                    <Upload style={{ width: '2rem', height: '2rem', color: 'var(--text-faint)', margin: '0 auto 0.5rem' }} />
                    <p className="text-muted" style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      Drag and drop an image here, or
                    </p>
                    <div className="flex items-center justify-center gap-3">
                      <label style={{ cursor: 'pointer' }}>
                        <span className="btn btn-sm" style={{ border: '1px solid var(--border)' }}>
                          <Upload style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                          Browse Files
                        </span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                      </label>
                      <span className="text-muted" style={{ fontSize: '0.875rem' }}>or</span>
                      <button
                        type="button"
                        onClick={takeScreenshot}
                        className="btn btn-sm"
                        style={{ border: '1px solid var(--border)' }}
                      >
                        <Camera style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                        Take Screenshot
                      </button>
                    </div>
                    <p className="text-faint" style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                      PNG, JPG, GIF, WEBP up to 5MB
                    </p>
                  </div>
                </div>
              ) : (
                <div style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  padding: '1rem',
                }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div style={{
                        width: '2.5rem',
                        height: '2.5rem',
                        background: 'color-mix(in srgb, var(--success) 15%, transparent)',
                        borderRadius: 'var(--radius)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <CheckCircle style={{ width: '1.25rem', height: '1.25rem', color: 'var(--success)' }} />
                      </div>
                      <div>
                        <p className="font-medium" style={{ fontSize: '0.875rem' }}>
                          {formData.screenshot.name}
                        </p>
                        <p className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {(formData.screenshot.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeScreenshot}
                      className="btn btn-ghost btn-icon btn-sm"
                    >
                      <X style={{ width: '1rem', height: '1rem' }} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-3" style={{ paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
              <button
                type="button"
                onClick={onClose}
                className="btn"
                style={{ border: '1px solid var(--border)' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formData.subject.trim() || !formData.message.trim()}
                className="btn btn-primary"
              >
                {isSubmitting ? (
                  <>
                    <div style={{
                      width: '1rem',
                      height: '1rem',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 0.7s linear infinite',
                      marginRight: '0.5rem',
                    }} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                    Send Support Request
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default SupportForm
